<template>
  <div
    class="cms-switch"
    :class="[
      {
        'is-disabled': disabled,
        'is-loading': loading,
        [`cms-switch--${size}`]: size
      },
      switchClass
    ]"
    role="switch"
    :aria-checked="checked"
    :aria-disabled="disabled"
    @click.prevent="handleClick"
  >
    <input
      v-if="name"
      :name="name"
      type="checkbox"
      class="cms-switch__input"
      :value="modelValue"
      :disabled="disabled"
    />
    <span class="cms-switch__core" :style="coreStyle">
      <div class="cms-switch__action">
        <i v-if="loading" class="cms-icon-loading">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
            <path
              fill="currentColor"
              d="M512 64a32 32 0 0 1 32 32v192a32 32 0 0 1-64 0V96a32 32 0 0 1 32-32m0 640a32 32 0 0 1 32 32v192a32 32 0 1 1-64 0V736a32 32 0 0 1 32-32m448-192a32 32 0 0 1-32 32H736a32 32 0 1 1 0-64h192a32 32 0 0 1 32 32m-640 0a32 32 0 0 1-32 32H96a32 32 0 0 1 0-64h192a32 32 0 0 1 32 32M195.2 195.2a32 32 0 0 1 45.248 0L376.32 331.008a32 32 0 0 1-45.248 45.248L195.2 240.448a32 32 0 0 1 0-45.248zm452.544 452.544a32 32 0 0 1 45.248 0L828.8 783.552a32 32 0 0 1-45.248 45.248L647.744 692.992a32 32 0 0 1 0-45.248zM828.8 195.264a32 32 0 0 1 0 45.184L692.992 376.32a32 32 0 0 1-45.248-45.248l135.808-135.808a32 32 0 0 1 45.248 0m-452.544 452.48a32 32 0 0 1 0 45.248L240.448 828.8a32 32 0 0 1-45.248-45.248l135.808-135.808a32 32 0 0 1 45.248 0z"
            ></path>
          </svg>
        </i>
      </div>
    </span>
    <span
      v-if="activeText || inactiveText"
      class="cms-switch__label"
      :class="{ 'is-active': checked }"
    >
      <span v-if="checked">{{ activeText }}</span>
      <span v-else>{{ inactiveText }}</span>
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { switchProps, switchEmits } from './switchs'

// 使用defineOptions API来定义组件选项
defineOptions({
  name: 'cms-switchs'
})

const props = defineProps(switchProps)
const emit = defineEmits(switchEmits)

// 是否选中状态
const checked = computed(() => props.modelValue === props.activeValue)

// 开关样式类
const switchClass = computed(() => ({
  'is-checked': checked.value
}))

// 开关核心样式
const coreStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width) {
    style.width = `${props.width}px`
  }
  if (checked.value) {
    style['border-color'] = props.activeColor
    style['background-color'] = props.activeColor
  } else {
    style['border-color'] = props.inactiveColor
    style['background-color'] = props.inactiveColor
  }
  return style
})

// 处理点击事件
const handleClick = () => {
  if (props.disabled || props.loading) return
  const value = checked.value ? props.inactiveValue : props.activeValue
  emit('update:modelValue', value)
  emit('change', value)
  emit('input', value)
}
</script>

<style>
.cms-switch {
  --cms-switch-on-color: var(--cms-primary-color, #409eff);
  --cms-switch-off-color: var(--cms-border-color-base, #dcdfe6);

  display: inline-flex;
  align-items: center;
  position: relative;
  font-size: 14px;
  line-height: 20px;
  vertical-align: middle;
  cursor: pointer;
}

.cms-switch.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.cms-switch__core {
  display: inline-block;
  position: relative;
  width: 40px;
  height: 20px;
  border: 1px solid var(--cms-switch-off-color);
  outline: none;
  border-radius: 10px;
  box-sizing: border-box;
  background: var(--cms-switch-off-color);
  transition: border-color 0.3s, background-color 0.3s;
}

.cms-switch__core .cms-switch__action {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 16px;
  height: 16px;
  border-radius: 100%;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;
}

.cms-switch.is-checked .cms-switch__core {
  border-color: var(--cms-switch-on-color);
  background-color: var(--cms-switch-on-color);
}

.cms-switch.is-checked .cms-switch__core .cms-switch__action {
  left: 21px;
}

.cms-switch--large {
  font-size: 14px;
  line-height: 24px;
}

.cms-switch--large .cms-switch__core {
  width: 48px;
  height: 24px;
  border-radius: 12px;
}

.cms-switch--large .cms-switch__core .cms-switch__action {
  width: 20px;
  height: 20px;
}

.cms-switch--large.is-checked .cms-switch__core .cms-switch__action {
  left: 25px;
}

.cms-switch--small {
  font-size: 12px;
  line-height: 16px;
}

.cms-switch--small .cms-switch__core {
  width: 32px;
  height: 16px;
  border-radius: 8px;
}

.cms-switch--small .cms-switch__core .cms-switch__action {
  width: 12px;
  height: 12px;
}

.cms-switch--small.is-checked .cms-switch__core .cms-switch__action {
  left: 17px;
}

.cms-switch__label {
  margin-left: 8px;
  font-size: 14px;
  color: var(--cms-text-color-regular, #606266);
}

.cms-switch__input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  margin: 0;
}

.cms-switch.is-loading .cms-switch__core .cms-switch__action {
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.cms-icon-loading {
  display: flex;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  color: rgb(64, 158, 255);
  animation: rotating 2s linear infinite;
}
</style>
